import invariant from 'invariant';
import Cookies from 'js-cookie';

import isFeatureEnabled from 'dpl/util/isFeatureEnabled';
import {
  isAdTrackingCookieSet,
  isAdTrackingOptedOut,
  isGPCEnabled
} from 'dpl/util/adPreferences';
import {
  EVENT_CATEGORIES,
  EVENT_ACTION_IDS,
  ANALYTICS_PROVIDERS,
  ANALYTICS_EVENTS,
  ANALYTICS_PLATFORM
} from 'dpl/constants/analytics';
import { FEATURES } from 'dpl/shared/constants/features';
import { GPC_DETECTED_SENT_KEY } from 'dpl/shared/constants/localStorage';
import { getAnalyticsOrigin } from 'dpl/shared/utils/getAnalyticsOrigin';
import {
  getLocalStorageValue,
  setLocalStorageValue
} from 'dpl/shared/utils/localStorage';
import { getUserTimeZone } from 'dpl/shared/utils/date';
import { removeEmptyValues } from 'dpl/shared/utils/object';

const timezone = getUserTimeZone();

export function sendGAEvent(
  eventAction: string,
  opts: Record<string, any> = {}
) {
  if (isAdTrackingOptedOut()) {
    return Promise.resolve();
  }

  return new Promise<void>(resolve => {
    if (typeof window.gtag !== 'function') {
      resolve();
      return;
    }

    setTimeout(resolve, 1000);

    window.gtag('event', eventAction, opts);
  });
}

interface RudderPageOpts {
  category?: string;
  name?: string;
  properties?: Record<string, any>;
}

export function sendPageview(
  opts: Record<string, any>,
  rudderOpts?: RudderPageOpts
) {
  // Don't log page view events from bots or admin users in prod
  if (
    window.__DPL_BOT_UA ||
    (window.__GD_CURRENT_ADMIN_USER_ID !== '' &&
      window.__GD_ENV_PROD !== 'false')
  ) {
    return;
  }

  if (typeof window.gtag === 'function') {
    window.gtag('config', window.__DPL_GA_TRACKING_ID ?? '', {
      ...window.__DPL_GA_OPTIONS,
      ...opts
    });
  }

  if (window.rudderanalytics) {
    const { category, name, properties } = rudderOpts || {};

    const props = {
      ...properties,
      admin_user_id: window.__GD_CURRENT_ADMIN_USER_ID
    };

    window.rudderanalytics.page(category, name, props);
  }
}

export function sendFBEvent(eventName: string) {
  if (isAdTrackingOptedOut()) {
    return;
  }

  if (typeof window.fbq === 'function') {
    window.fbq('track', eventName);
  }
}

export function sendBingEvent(
  eventAction: string,
  opts: Record<string, any> = {}
) {
  if (isAdTrackingOptedOut()) {
    return;
  }

  if (window.uetq) {
    window.uetq.push('event', eventAction, opts);
  }
}

export function sendRudderstackEvent(
  event: string,
  properties: Record<string, any> = {}
) {
  invariant(
    Object.values(ANALYTICS_EVENTS).includes(event),
    `Event must exist in ANALYTICS_EVENTS constant. ${event}`
  );

  const defaultProps = window.__GD_ANALYTICS_TRACK_DEFAULT_PROPS || {};
  const analyticsOrigin = {
    ...getAnalyticsOrigin(),
    ...removeEmptyValues({
      origin_view: properties.origin_view || null,
      origin_view_feature: properties.origin_view_feature || null,
      origin_view_feature_location:
        properties.origin_view_feature_location || null
    })
  };

  if (window.rudderanalytics) {
    const trackProperties = {
      ...properties,
      ...defaultProps,
      ...analyticsOrigin,
      app_version: CONFIG.appVersion,
      platform: ANALYTICS_PLATFORM,
      timezone
    };

    // Defensively avoid sending Facebook advertising identifiers when the user
    // has opted out of ad tracking, even if a destination-level consent mapping
    // is missed in the RudderStack dashboard.
    if (isAdTrackingOptedOut()) {
      window.rudderanalytics.track(event, trackProperties);
    } else {
      const cookies = Cookies.get();

      window.rudderanalytics.track(event, trackProperties, {
        fbc: cookies._fbc,
        fbp: cookies._fbp
      });
    }
  }
}

/**
 * Fire a `GPC Signal Detected` event on boot when the browser sends a Global
 * Privacy Control signal. Deduped to once per browser via a localStorage flag
 * so we don't re-report the signal on every page view for GPC users.
 */
export function trackGpcSignalDetectedOnce() {
  if (!isGPCEnabled()) {
    return;
  }

  if (getLocalStorageValue(GPC_DETECTED_SENT_KEY)) {
    return;
  }

  sendRudderstackEvent(ANALYTICS_EVENTS.GPC_SIGNAL_DETECTED, {
    // Whether the user had also explicitly persisted the opt-out cookie, as
    // opposed to being opted out purely by the GPC signal.
    already_opted_out: isAdTrackingCookieSet()
  });

  setLocalStorageValue(GPC_DETECTED_SENT_KEY, true);
}

export function sendRudderstackGroupEvent(
  groupId: string,
  traits: Record<string, any> = {}
) {
  if (window.rudderanalytics) {
    window.rudderanalytics.group(groupId, {
      ...traits,
      app_version: CONFIG.appVersion,
      platform: ANALYTICS_PLATFORM,
      timezone
    });
  }
}

export function sendAnalyticsEvent(
  event: string,
  properties: Record<string, any> = {}
) {
  invariant(
    Object.values(ANALYTICS_EVENTS).includes(event),
    `Event must exist in ANALYTICS_EVENTS constant. ${event}`
  );

  if (window.analytics) {
    window.analytics.track(event, {
      ...properties,
      app_version: CONFIG.appVersion,
      platform: ANALYTICS_PLATFORM,
      timezone
    });
  }

  sendRudderstackEvent(event, properties);
}

// TODO: just send GA events directly now that we no longer use ahoy
export function sendAhoyAndGAEvents(
  eventAction: string,
  opts: Record<string, any> = {}
) {
  sendGAEvent(eventAction, opts);
}

export function trackEvent(
  eventCategory: string,
  eventAction: string = 'click',
  opts?: Record<string, any>,
  providers: string[] = [ANALYTICS_PROVIDERS.GA]
) {
  invariant(
    Object.values(EVENT_CATEGORIES).includes(eventCategory),
    'Event category must exist in EVENT_CATEGORIES constant.'
  );

  return (fn: Function) =>
    function eventTrackingFn(this: any, e: any, ...args: any[]) {
      if (!eventAction) {
        eventAction = e && typeof e.type === 'string' ? e.type : 'click';
      }

      const finalOpts = {
        event_category: eventCategory,
        ...opts
      };

      if (providers.includes(ANALYTICS_PROVIDERS.GA)) {
        sendGAEvent(eventAction, finalOpts);
      }

      if (providers.includes(ANALYTICS_PROVIDERS.BING)) {
        sendBingEvent(eventAction, finalOpts);
      }

      if (providers.includes(ANALYTICS_PROVIDERS.FB)) {
        sendFBEvent(eventCategory);
      }

      return fn.call(this, e, ...args);
    };
}

export function trackConversion(actionId: string) {
  invariant(
    Object.values(EVENT_ACTION_IDS).includes(actionId),
    'Event actionId must exist in EVENT_ACTION_IDS constant.'
  );

  return (fn: Function) =>
    function conversionTrackingFn(this: any, ...args: any[]) {
      // Skip conversion tracking when opted out, but still run the wrapped
      // function so the underlying user action is not blocked.
      if (!isAdTrackingOptedOut()) {
        sendGAEvent('conversion', { send_to: actionId });
      }

      return fn.call(this, ...args);
    };
}

export function trackConversionV2(actionId: string) {
  invariant(
    Object.values(EVENT_ACTION_IDS).includes(actionId),
    'Event actionId must exist in EVENT_ACTION_IDS constant.'
  );

  if (isAdTrackingOptedOut()) {
    return;
  }

  sendGAEvent('conversion', { send_to: actionId });
}

interface IdentifyParams {
  userId?: string | number | null;
  traits?: Record<string, any>;
}

export function sendAnalyticsIdentify({ userId, traits }: IdentifyParams) {
  if (!userId) {
    return;
  }

  // do not call identify when its an admin user impersonating
  // someone in production
  if (window.__GD_CURRENT_USER_ID !== '' && window.__GD_ENV_PROD === 'true') {
    return;
  }

  if (window.rudderanalytics) {
    window.rudderanalytics.identify(`${userId}`, traits);
  }
}

export function analyticsReset() {
  if (window.analytics) {
    window.analytics.reset();
  }
}

export function collectTrackedExperiments(
  features: Record<string, any> = FEATURES
) {
  const regex = /^EXP(ERIMENT)?_/;

  return Object.keys(features)
    .filter(feature => regex.test(feature))
    .map(experimentalFeature => {
      return {
        experiment_name: experimentalFeature.replace(regex, ''),
        treatment: isFeatureEnabled(features[experimentalFeature])
          ? 'test'
          : 'control'
      };
    });
}

interface TrackPageViewParams {
  name: string;
  category: string;
  properties: Record<string, any>;
}

export function trackPageView({
  name,
  category,
  properties
}: TrackPageViewParams) {
  sendPageview(
    {
      page_title: document.title,
      page_path: `${window.location.pathname}${window.location.search}`,
      page_location: window.location.href
    },
    { name, category, properties }
  );
}
